import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { platformMeta } from '../platformMeta';
import { SocialPlatform } from '../types';
import { ComposerTab } from './useComposerDraft';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Promote pillar; each part uses its own slice of it */
import styles from '../Promote.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ComposerChannelTabsProps {
  platforms: SocialPlatform[];
  /** The platforms that carry copy of their own. */
  overridden: SocialPlatform[];
  active: ComposerTab;
  onChange: (tab: ComposerTab) => void;
}

/** Master copy first, then one tab per channel the post goes to. */
const ComposerChannelTabsComponent: React.FC<ComposerChannelTabsProps> = ({
  platforms,
  overridden,
  active,
  onChange,
}) => {
  const intl = useIntl();

  const tabs = useMemo(
    () =>
      platforms.map((platform) => {
        const meta = platformMeta[platform];
        const hasOwnCopy = overridden.includes(platform);
        return (
          <button
            key={platform}
            type="button"
            aria-current={active === platform ? 'true' : undefined}
            className={clsx(styles.copyTab, active === platform && styles.copyTabActive)}
            onClick={() => onChange(platform)}
          >
            <span
              className={styles.copyTabDot}
              style={{ background: hasOwnCopy ? meta.color : 'var(--tx-3)' }}
              aria-hidden="true"
            />
            {meta.name}
          </button>
        );
      }),
    [active, onChange, overridden, platforms]
  );

  return (
    <div className={styles.copyTabs} role="tablist">
      <button
        type="button"
        aria-current={active === 'master' ? 'true' : undefined}
        className={clsx(styles.copyTab, active === 'master' && styles.copyTabActive)}
        onClick={() => onChange('master')}
      >
        {intl.formatMessage({ id: 'app.sml.promote.composer.tab.master', defaultMessage: 'Master copy' })}
      </button>
      {tabs}
    </div>
  );
};

export const ComposerChannelTabs = TypedMemo(ComposerChannelTabsComponent);
